import React from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Box, Checkbox, FormControlLabel, FormGroup, Divider, Typography
} from "@mui/material";
import ImagePreviewBox from "../../components/common/ImagePreviewBox";
import { DocStatus, VerifiedForm } from "./driverTypes";
import { evaluateApprovalConditions, isApprovable } from "../../utils/driverApproval";

interface Props {
  open: boolean;
  onClose: () => void;
  form: VerifiedForm;
  setForm: React.Dispatch<React.SetStateAction<VerifiedForm>>;
  onApprove: () => void;
}

/**
 * Mandatory-docs approval modal (RC / DL / Profile photo). The "verify all"
 * master checkbox flips the three statuses at once.
 *
 * Two gates, deliberately:
 *  - what the DRIVER did (utils/driverApproval — the five the BACKEND enforces on every surface);
 *  - what OPS did — this reviewer confirming each document against the images above.
 *
 * The second is a dashboard-only policy on top of the server rule, never a replacement: it is
 * strictly stricter, so this screen can never approve something the backend would refuse. It also
 * means a document explicitly set to REJECTED keeps Approve disabled, so approval cannot silently
 * overwrite that rejection.
 *
 * The ManagerApp has no second gate — an agent standing with the driver has no images to review,
 * and their press is what marks the three verified.
 */
const DriverApproveModal: React.FC<Props> = ({ open, onClose, form, setForm, onApprove }) => {
  const conditions = evaluateApprovalConditions(form);

  const opsChecks = [
    { key: 'rcReviewed', label: 'RC verified by you', met: form.rcStatus === DocStatus.VERIFIED },
    { key: 'dlReviewed', label: 'DL verified by you', met: form.dlStatus === DocStatus.VERIFIED },
    { key: 'photoReviewed', label: 'Profile photo verified by you', met: form.isProfilePhotoVerified === true },
  ];

  const isApproveEnabled = isApprovable(form) && opsChecks.every((c) => c.met);

  return (
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" disableEnforceFocus>
        <DialogTitle>Approve Driver (Mandatory Docs)</DialogTitle>
        <DialogContent>
          {/* Master toggle — verify all three mandatory docs at once (the
              individual dropdowns below still work and stay in sync). */}
          <FormControlLabel
              sx={{ mb: 1, mt: 1 }}
              control={
                <Checkbox
                    checked={
                      form.rcStatus === DocStatus.VERIFIED &&
                      form.dlStatus === DocStatus.VERIFIED &&
                      form.isProfilePhotoVerified === true
                    }
                    onChange={(e) => {
                      const v = e.target.checked;
                      setForm(prev => ({
                        ...prev,
                        rcStatus: v ? DocStatus.VERIFIED : DocStatus.PENDING,
                        dlStatus: v ? DocStatus.VERIFIED : DocStatus.PENDING,
                        isProfilePhotoVerified: v,
                      }));
                    }}
                />
              }
              label={<Typography fontWeight={700}>Verify all mandatory documents (RC, DL &amp; Photo)</Typography>}
          />
          <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                gap: 3,
                alignItems: 'center',
                py: 2,
              }}
          >
            {/* Row 1: RC (Restricted to VERIFIED / REJECTED) */}
            <TextField
                label="License Plate"
                value={form.vehicleLicensePlateNumber}
                fullWidth
                disabled
            />
            <ImagePreviewBox imageUrls={form.cImageUrl} altText="RC" />
            <TextField
                select
                label="RC Status"
                value={form.rcStatus === DocStatus.VERIFIED ? DocStatus.VERIFIED : (form.rcStatus === DocStatus.REJECTED ? DocStatus.REJECTED : form.rcStatus)}
                onChange={(e) => setForm(prev => ({ ...prev, rcStatus: e.target.value as DocStatus }))}
                fullWidth
                size="small"
            >
              <MenuItem value="" disabled><em>Select Status</em></MenuItem>
              <MenuItem value={DocStatus.VERIFIED}>VERIFIED</MenuItem>
              <MenuItem value={DocStatus.REJECTED}>REJECTED</MenuItem>
              <MenuItem value={DocStatus.PENDING}>PENDING</MenuItem>
              <MenuItem value={DocStatus.MISSING}>MISSING</MenuItem>
              <MenuItem value={DocStatus.EXPIRED}>EXPIRED</MenuItem>
            </TextField>

            {/* Row 2: DL (Restricted to VERIFIED / REJECTED) */}
            <TextField
                label="License Number"
                value={form.licenseNumber}
                fullWidth
                disabled
            />
            <ImagePreviewBox imageUrls={form.dlImageUrl} altText="DL" />
            <TextField
                select
                label="DL Status"
                value={form.dlStatus === DocStatus.VERIFIED ? DocStatus.VERIFIED : (form.dlStatus === DocStatus.REJECTED ? DocStatus.REJECTED : form.dlStatus)}
                onChange={(e) => setForm(prev => ({ ...prev, dlStatus: e.target.value as DocStatus }))}
                fullWidth
                size="small"
            >
              <MenuItem value="" disabled><em>Select Status</em></MenuItem>
              <MenuItem value={DocStatus.VERIFIED}>VERIFIED</MenuItem>
              <MenuItem value={DocStatus.REJECTED}>REJECTED</MenuItem>
              <MenuItem value={DocStatus.PENDING}>PENDING</MenuItem>
              <MenuItem value={DocStatus.MISSING}>MISSING</MenuItem>
              <MenuItem value={DocStatus.EXPIRED}>EXPIRED</MenuItem>
            </TextField>

            {/* Row 3: Profile (Restricted to VERIFIED / REJECTED) */}
            <TextField
                select
                label="Driver Available"
                value={form.isAvailable ? "yes" : "no"}
                onChange={e => setForm(prev => ({ ...prev, isAvailable: e.target.value === "yes" }))}
                fullWidth
                size="small"
            >
              <MenuItem value="yes">Available</MenuItem>
              <MenuItem value="no">Unavailable</MenuItem>
            </TextField>
            <ImagePreviewBox imageUrls={form.profilePhotoUrl} altText="Profile" />
            <TextField
                select
                label="Photo Status"
                value={form.isProfilePhotoVerified === true ? "VERIFIED" : (form.isProfilePhotoVerified === false ? "PENDING" : "")}
                onChange={e => setForm(prev => ({ ...prev, isProfilePhotoVerified: e.target.value === "VERIFIED" }))}
                fullWidth
                size="small"
            >
              <MenuItem value="" disabled><em>Select Status</em></MenuItem>
              <MenuItem value="VERIFIED">VERIFIED</MenuItem>
              <MenuItem value="PENDING">PENDING</MenuItem>
            </TextField>
          </Box>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
            Done by the driver
          </Typography>
          <FormGroup row sx={{ gap: 2 }}>
            {conditions.map((c) => (
                <FormControlLabel
                    key={c.key}
                    control={<Checkbox checked={c.met} readOnly disableRipple />}
                    label={c.label}
                />
            ))}
          </FormGroup>

          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>
            Your review — set each status above after checking the images
          </Typography>
          <FormGroup row sx={{ gap: 2 }}>
            {opsChecks.map((c) => (
                <FormControlLabel
                    key={c.key}
                    control={<Checkbox checked={c.met} readOnly disableRipple />}
                    label={c.label}
                />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>
            Cancel
          </Button>
          <Button
              variant="contained"
              disabled={!isApproveEnabled}
              onClick={onApprove}
          >
            Approve Driver
          </Button>
        </DialogActions>
      </Dialog>
  );
};

export default DriverApproveModal;
